import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Activity, FileText, DollarSign, ShoppingCart, Package,
  Truck, Factory, Users, FolderKanban, UserPlus, Sparkles,
  AlertTriangle, CheckCircle, Info, XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import useWebSocket from '../../hooks/useWebSocket';

const ICON_MAP = {
  Activity, FileText, DollarSign, ShoppingCart, Package,
  Truck, Factory, Users, FolderKanban, UserPlus, Sparkles,
};

const SEVERITY_STYLES = {
  info:    { icon: Info,          dot: 'bg-blue-500',    text: 'text-blue-700 dark:text-blue-300',    ring: 'ring-blue-100 dark:ring-blue-900/40' },
  success: { icon: CheckCircle,   dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-100 dark:ring-emerald-900/40' },
  warning: { icon: AlertTriangle, dot: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300',  ring: 'ring-amber-100 dark:ring-amber-900/40' },
  danger:  { icon: XCircle,       dot: 'bg-red-500',     text: 'text-red-700 dark:text-red-300',      ring: 'ring-red-100 dark:ring-red-900/40' },
  error:   { icon: XCircle,       dot: 'bg-red-500',     text: 'text-red-700 dark:text-red-300',      ring: 'ring-red-100 dark:ring-red-900/40' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 45) return 'الآن';
  if (diffSec < 3600) return `منذ ${Math.floor(diffSec / 60)} د`;
  if (diffSec < 86400) return `منذ ${Math.floor(diffSec / 3600)} س`;
  const days = Math.floor(diffSec / 86400);
  if (days < 7) return `منذ ${days} يوم`;
  return new Date(iso).toLocaleDateString('ar-AE');
}

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  'X-Company-ID': localStorage.getItem('company_id') || '',
});

/**
 * ActivityFeed — live log of cross-module events.
 *
 * Props:
 *   limit        number — max events to show (default 10)
 *   pollMs       number — fallback refresh interval in ms (0 = no polling, default 60000).
 *                Live updates arrive via WebSocket; polling only covers the
 *                case where the socket is disconnected.
 *   showHeader   boolean — render title header (default true)
 *   className    string
 */
export default function ActivityFeed({
  limit = 10,
  pollMs = 60000,
  showHeader = true,
  className = '',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeed = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await axios.get(
        `${base}/api/activity-feed?limit=${limit}`,
        { headers: getAuthHeaders() },
      );
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'تعذر تحميل سجل النشاط');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchFeed();
    if (pollMs > 0) {
      const id = setInterval(fetchFeed, pollMs);
      return () => clearInterval(id);
    }
  }, [fetchFeed, pollMs]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.event !== 'activity' || !msg.data) return;
    const d = msg.data;
    const newItem = {
      id: d.id,
      event_type: d.event_type,
      label_ar: d.label_ar || d.event_type,
      message_ar: d.payload?.message_ar || d.label_ar || d.event_type,
      severity: d.severity || 'info',
      created_at: d.created_at,
      icon: d.icon,
      link: d.link,
      _justArrived: true,
    };
    setItems((prev) => {
      const without = prev.filter((x) => x.id !== newItem.id);
      return [newItem, ...without].slice(0, limit);
    });
    setTimeout(() => {
      setItems((prev) =>
        prev.map((x) => (x.id === newItem.id ? { ...x, _justArrived: false } : x)),
      );
    }, 2000);
  }, [limit]);

  const { connected } = useWebSocket({
    filterEvents: ['activity'],
    onMessage: handleWsMessage,
  });

  return (
    <div
      className={cn(
        'rounded-lg bg-white dark:bg-neutral-900',
        'border border-neutral-200 dark:border-neutral-800 shadow-sm',
        className,
      )}
      dir="rtl"
    >
      {showHeader && (
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-neutral-100 dark:border-neutral-800">
          <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
            <Activity size={16} className="text-primary-600 dark:text-primary-400" aria-hidden="true" />
            آخر الأحداث
          </h3>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1',
                connected
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
              )}
              title={connected ? 'متصل عبر WebSocket' : 'غير متصل — يعمل polling احتياطي'}
            >
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full',
                  connected ? 'bg-emerald-500 animate-pulse' : 'bg-neutral-400',
                )}
              />
              {connected ? 'مباشر' : 'غير متصل'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {loading ? '...' : `${items.length} حدث`}
            </span>
          </div>
        </div>
      )}

      <div className="px-2 py-2 max-h-[440px] overflow-y-auto">
        {loading && items.length === 0 && (
          <ul className="space-y-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="animate-pulse flex items-start gap-3 px-3 py-2">
                <span className="h-8 w-8 rounded-lg bg-neutral-200 dark:bg-neutral-800 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <span className="block h-3 w-3/4 rounded bg-neutral-200 dark:bg-neutral-800" />
                  <span className="block h-2.5 w-1/3 rounded bg-neutral-100 dark:bg-neutral-800" />
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && error && (
          <div className="p-6 text-center text-sm text-danger-600 dark:text-danger-400">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="p-8 text-center">
            <Activity size={28} className="mx-auto mb-2 text-neutral-300 dark:text-neutral-700" aria-hidden="true" />
            <p className="text-sm text-neutral-500 dark:text-neutral-400">لا يوجد نشاط حديث</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {items.map((item) => {
              const sev = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.info;
              const Icon = ICON_MAP[item.icon] || Activity;
              return (
                <li key={item.id} className="group">
                  <Link
                    to={item.link || '/dashboard'}
                    className={cn(
                      'flex items-start gap-3 px-3 py-3 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors',
                      item._justArrived && 'ring-2 ring-primary-400 bg-primary-50/60 dark:bg-primary-900/20 animate-pulse',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0 ring-1 ring-inset',
                        sev.ring,
                      )}
                      aria-hidden="true"
                    >
                      <Icon size={16} className={sev.text} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <span className={cn('text-sm font-semibold truncate', sev.text)}>
                          {item.label_ar}
                        </span>
                        <span className="text-[11px] text-neutral-400 dark:text-neutral-500 whitespace-nowrap flex-shrink-0">
                          {relativeTime(item.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5 line-clamp-2">
                        {item.message_ar}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
